import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import gtmService, { GtmOverview } from '../services/gtmService';
import { PageHeader } from '../components/ui';
import { routeById, findRoute, darfSehen } from '../navigation/routes';
import { gtmPfad } from '../vertrieb/gtmLinks';
import { useAuth } from '../contexts/AuthContext';

/**
 * Ziel nur verlinken, wenn die Person die Seite oeffnen darf (Welle 2: sonst
 * landet sie auf "Kein Zugriff"). Die Rechte kommen aus navigation/routes.ts.
 */
const useDarfOeffnen = () => {
  const { user } = useAuth();
  return (pfad: string) => {
    const eintrag = findRoute(pfad);
    return !eintrag || darfSehen(user, eintrag);
  };
};

const Stat: React.FC<{ label: string; value: number; to?: string; accent?: string }> = ({ label, value, to, accent }) => {
  const karte = (
    <div className="card" style={{ textAlign: 'center', cursor: to ? 'pointer' : 'default' }}>
      <div style={{ fontSize: '28px', fontWeight: 700, color: accent || 'var(--sec-fg)' }}>{value}</div>
      <div style={{ fontSize: '13px', color: 'var(--sec-fg-muted)', marginTop: '4px' }}>{label}</div>
    </div>
  );
  return to
    ? <Link to={to} style={{ textDecoration: 'none', flex: '1 1 150px' }}>{karte}</Link>
    : <div style={{ flex: '1 1 150px' }}>{karte}</div>;
};

// The outbound chain — each step links to the module that covers it.
const CHAIN: { label: string; to: string }[] = [
  { label: 'Zielkunden, Personas & Angebote (GTM-Setup)', to: '/gtm-setup' },
  { label: 'Nutzenversprechen (Value Props)', to: '/value-props' },
  { label: 'Marktrecherche (Sales Research)', to: '/research' },
  { label: 'Zielfirmenlisten (Listbuilding)', to: '/list-building' },
  { label: 'Leads nach Zielkundenprofil (ICP)', to: '/icp-campaigns' },
  { label: 'Kampagnen-Architekt', to: '/campaign-architect' },
  { label: 'Kaltakquise-Texte (Cold-Copy Studio)', to: '/copywriting-studio' },
  { label: 'Kaufsignale', to: '/signal-campaigns' },
  { label: 'Antworten einordnen (Reply Handler)', to: '/reply-handler' },
  { label: 'Kampagnen-Kennzahlen (Outbound)', to: '/outbound-analysis' },
  { label: 'Pipeline-Auswertung (Forecast)', to: '/pipeline-analysis' },
];

const GtmOverviewPage: React.FC = () => {
  const [data, setData] = useState<GtmOverview | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const darfOeffnen = useDarfOeffnen();
  const ziel = (pfad: string) => (darfOeffnen(pfad) ? pfad : undefined);
  const kette = CHAIN.filter((step) => darfOeffnen(step.to));

  useEffect(() => {
    gtmService
      .getOverview()
      .then(setData)
      .catch((err) => setError(err.response?.data?.error || 'Fehler beim Laden der Übersicht'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="page-container">
      <PageHeader title="Markteintritt: Übersicht (GTM)" subtitle={routeById('gtm-overview')?.description} />
      <p style={{ color: 'var(--sec-fg-muted)', marginBottom: '24px' }}>
        Zentraler Einstieg in die Outbound-Suite (powered by den lemlist-Skills). Kennzahlen sind
        klickbar und führen direkt ins jeweilige Modul.
      </p>

      {error && <div className="alert alert-error">{error}</div>}
      {loading && <div className="loading-text">Lade…</div>}

      {data && (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '8px' }}>
            <Stat label="ICPs" value={data.icps} to={ziel('/gtm-setup') && gtmPfad('icp')} accent="var(--sec-accent)" />
            <Stat label="Personas" value={data.personas} to={ziel('/gtm-setup') && gtmPfad('persona')} accent="var(--sec-accent)" />
            <Stat label="Angebote" value={data.offers} to={ziel('/gtm-setup') && gtmPfad('offer')} accent="var(--sec-accent)" />
            <Stat label="Leads" value={data.leads} to={ziel('/lead-sync')} />
            <Stat label="Zielfirmen recherchieren (KI)" value={data.salesCampaigns} to={ziel('/sales-campaigns')} />
            <Stat label="Kaufsignale" value={data.signalCampaigns} to={ziel('/signal-campaigns')} />
            <Stat label="Leads nach Zielkundenprofil (ICP)" value={data.icpCampaigns} to={ziel('/icp-campaigns')} accent="var(--sec-accent)" />
            <Stat
              label="Offene Antworten"
              value={data.unhandledReplies}
              to={ziel('/reply-handler')}
              accent={data.unhandledReplies > 0 ? 'var(--sec-danger)' : 'var(--sec-success)'}
            />
          </div>

          <div className="card" style={{ marginTop: '16px' }}>
            <div className="card-title" style={{ marginBottom: '12px' }}>Outbound-Kette</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px' }}>
              {kette.map((step, i) => (
                <React.Fragment key={step.to + i}>
                  <Link
                    to={step.to}
                    className="btn btn-secondary btn-sm"
                    style={{ textDecoration: 'none' }}
                  >
                    {step.label}
                  </Link>
                  {i < kette.length - 1 && <span style={{ color: 'var(--sec-fg-subtle)' }}>→</span>}
                </React.Fragment>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default GtmOverviewPage;
